import React from 'react'
import Image from 'next/image'

interface RichTextNode {
  type?: string
  children?: RichTextNode[]
  text?: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  code?: boolean
  strikethrough?: boolean
  url?: string
  rel?: string
  target?: string
  format?: string | number
  tag?: string
  // Image/upload fields
  value?: {
    url?: string
    alt?: string
    width?: number
    height?: number
    filename?: string
  }
  relationTo?: string
  // Lexical specific
  version?: number
  fields?: Record<string, any>
}

interface RichTextData {
  root?: {
    children?: RichTextNode[]
  }
}

// Extract plain text from a node tree (for generating heading IDs)
function extractText(node: RichTextNode): string {
  if (node.text) return node.text
  if (!node.children) return ''
  return node.children.map(extractText).join('')
}

// Generate a URL-friendly slug from text
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

function renderNode(node: RichTextNode | string, index: number): React.ReactNode {
  if (typeof node === 'string') {
    return node
  }

  if (!node || typeof node !== 'object') {
    return null
  }

  const { type, children = [] } = node

  switch (type) {
    case 'heading': {
      // La page porte déjà son <h1> (titre de l'article) : un « h1 » dans le
      // corps devient un h2. Sept articles en avaient deux (bilan SEO du 29/09/2026).
      const brut = (node.tag || 'h2').toLowerCase()
      const level = brut === 'h1' ? 'h2' : brut
      const HeadingTag = level as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      const headingText = extractText(node)
      const id = slugify(headingText)
      const headingClasses: Record<string, string> = {
        h1: 'text-3xl font-bold mt-10 mb-4 text-gray-900',
        h2: 'text-2xl font-bold mt-8 mb-3 text-gray-900',
        h3: 'text-xl font-semibold mt-6 mb-2 text-gray-800',
        h4: 'text-lg font-semibold mt-5 mb-2 text-gray-800',
        h5: 'text-base font-semibold mt-4 mb-2 text-gray-800',
        h6: 'text-sm font-semibold mt-3 mb-2 text-gray-800',
      }
      return (
        <HeadingTag key={index} id={id} className={headingClasses[level]} style={{ scrollMarginTop: '100px' }}>
          {children.map((child, i) => renderNode(child, i))}
        </HeadingTag>
      )
    }

    case 'paragraph':
      return (
        <p key={index} className="text-gray-600 leading-relaxed mb-4 text-[16px]">
          {children.length > 0 ? children.map((child, i) => renderNode(child, i)) : null}
        </p>
      )

    case 'list': {
      const listType = node.tag === 'ol' ? 'ol' : 'ul'
      const ListTag = listType as 'ul' | 'ol'
      const listClasses = listType === 'ol' ? 'list-decimal' : 'list-disc'
      return (
        <ListTag key={index} className={`${listClasses} list-inside mb-4 pl-4 space-y-1`}>
          {children.map((child, i) => renderNode(child, i))}
        </ListTag>
      )
    }

    case 'listitem':
      return (
        <li key={index} className="text-gray-600 leading-relaxed">
          {children.map((child, i) => renderNode(child, i))}
        </li>
      )

    case 'link': {
      // Support both classic format (node.url) and Lexical format (node.fields.url)
      const url = node.url || node.fields?.url || '#'
      const isExternal = node.fields?.newTab || node.target === '_blank' || (typeof url === 'string' && url.startsWith('http'))
      const rel = node.rel || (isExternal ? 'noopener noreferrer' : undefined)
      const target = isExternal ? '_blank' : undefined
      return (
        <a
          key={index}
          href={url}
          rel={rel}
          target={target}
          className="text-forest font-semibold underline decoration-forest/40 decoration-2 underline-offset-2 hover:decoration-forest hover:text-forest-light transition-all"
        >
          {children.map((child, i) => renderNode(child, i))}
        </a>
      )
    }

    case 'quote':
      return (
        <blockquote key={index} className="border-l-4 border-forest/30 pl-6 py-2 my-6 text-gray-600 italic bg-forest/5 rounded-r-lg">
          {children.map((child, i) => renderNode(child, i))}
        </blockquote>
      )

    case 'code':
      return (
        <pre key={index} className="bg-gray-50 border border-gray-200 p-4 rounded-lg mb-4 overflow-auto">
          <code className="text-sm font-mono text-gray-800">
            {children.map((child, i) => renderNode(child, i))}
          </code>
        </pre>
      )

    // Lexical upload/image node
    case 'upload': {
      const media = node.value
      if (!media?.url) return null
      return (
        <figure key={index} className="my-6">
          <Image
            src={media.url}
            alt={media.alt || ''}
            width={media.width || 800}
            height={media.height || 500}
            className="w-full rounded-xl"
          />
          {media.alt && (
            <figcaption className="text-center text-sm text-gray-400 mt-2">{media.alt}</figcaption>
          )}
        </figure>
      )
    }

    case 'horizontalrule':
      return <hr key={index} className="border-gray-200 my-8" />

    case 'text': {
      let content: React.ReactNode = node.text || ''

      if (node.bold) {
        content = <strong className="font-semibold text-gray-900">{content}</strong>
      }
      if (node.italic) {
        content = <em className="italic">{content}</em>
      }
      if (node.underline) {
        content = <u className="underline">{content}</u>
      }
      if (node.strikethrough) {
        content = <s className="line-through">{content}</s>
      }
      if (node.code) {
        content = <code className="bg-gray-100 px-2 py-1 rounded font-mono text-sm text-forest">{content}</code>
      }

      return <span key={index}>{content}</span>
    }

    case 'root':
      return <>{children.map((child, i) => renderNode(child, i))}</>

    default:
      if (children.length > 0) {
        return <div key={index}>{children.map((child, i) => renderNode(child, i))}</div>
      }
      return null
  }
}

// Extract heading structure for table of contents
export interface TocItem {
  id: string
  text: string
  level: number
}

export function extractToc(data: RichTextData | null | undefined): TocItem[] {
  if (!data?.root?.children) return []

  const items: TocItem[] = []
  for (const node of data.root.children) {
    if (node.type === 'heading') {
      const tag = (node.tag || 'h2').toLowerCase()
      const level = parseInt(tag.replace('h', ''), 10)
      if (level <= 3) {
        const text = extractText(node)
        items.push({ id: slugify(text), text, level })
      }
    }
  }
  return items
}

interface RichTextRendererProps {
  data: RichTextData | null | undefined
}

export default function RichTextRenderer({ data }: RichTextRendererProps) {
  if (!data || !data.root || !data.root.children) {
    return null
  }

  return (
    <div className="rich-text-content">
      {data.root.children.map((node, index) => (
        <React.Fragment key={index}>{renderNode(node, index)}</React.Fragment>
      ))}
    </div>
  )
}
